import { useState } from "react";
import { Game, GameVersion } from "../../shared/api/client";
import BoxCard from "../../shared/components/BoxCard";
import { LabelValueInput } from "./LabelValueInput";
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import { useAuth } from "../../shared/auth/AuthContext";
import { api } from "../../shared/api/client";
import { useNavigate } from "react-router-dom";

export function CreateMatch({
  game,
  version,
  majorVersion,
}: {
  game: Game;
  version: GameVersion;
  majorVersion?: number;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [numPlayers, setNumPlayers] = useState<number>(2);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize options state with first choice as default for each configuration option
  const configOptions = version.configurationOptions ?? [];
  const [options, setOptions] = useState<Record<string, string>>(() => {
    const defaults: Record<string, string> = {};
    for (const opt of configOptions) {
      if (opt.config.choices.length > 0) {
        defaults[opt.fieldName] = opt.config.choices[0].id;
      }
    }
    return defaults;
  });

  const validate = () => {
    const errors = [];
    if (Number.isNaN(numPlayers)) {
      errors.push(`Requires a player count`);
    }
    if (numPlayers < version.minPlayers) {
      errors.push(`Requires at least ${version.minPlayers} players`);
    }
    if (numPlayers > version.maxPlayers) {
      errors.push(`At most ${version.maxPlayers} players are allowed`);
    }
    return errors;
  };

  const create = async () => {
    if (validate().length || !user) {
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const response = await api.createMatch({
        gameId: game.id,
        numPlayers,
        visibility: "public",
        majorVersion,
        options: configOptions.length > 0 ? options : undefined,
      });
      navigate(`/match/${response.match.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create match");
      setCreating(false);
    }
  };

  return (
    <BoxCard title={`Create ${version.name} match`}>
      <LabelValueInput
        label="Number of Players"
        value={numPlayers}
        onChange={setNumPlayers}
        type="number"
      />
      {configOptions.map((opt) => (
        <FormControl key={opt.fieldName} fullWidth size="small" sx={{ mt: 1 }}>
          <InputLabel>{opt.config.label}</InputLabel>
          <Select
            value={options[opt.fieldName] ?? ""}
            label={opt.config.label}
            onChange={(e) =>
              setOptions((prev) => ({
                ...prev,
                [opt.fieldName]: e.target.value,
              }))
            }
          >
            {opt.config.choices.map((choice) => (
              <MenuItem key={choice.id} value={choice.id}>
                {choice.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ))}
      {validate().map((validationError) => (
        <Typography key={validationError} sx={{ color: theme.palette.error.main }}>
          {validationError}
        </Typography>
      ))}
      {error && (
        <Typography sx={{ color: theme.palette.error.main }}>
          {error}
        </Typography>
      )}
      <Button
        variant="contained"
        onClick={create}
        disabled={validate().length > 0 || creating}
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          fontWeight: 600,
          mt: 1,
          alignSelf: "flex-start",
        }}
      >
        {creating ? "Creating..." : "Create"}
      </Button>
    </BoxCard>
  );
}
